import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, catchError, of } from 'rxjs';
import { utils } from '../helper/utils';
import { CodeDescription } from '../models/code-description.model';
import { AuditLog } from '../models/audit-log.model';
import { DownloadRequest } from '../models/download-filter.model';
import { RepairModeListItem } from '../models/repair-mode-list-item.model';
import { RepairModeFilter, RepairModeFilterFormValue } from '../models/repair-mode-filter.model';
import { CreateRepairModeRequest, UpdateRepairModeRequest } from '../models/create-repair-mode-request.model';

@Injectable({ providedIn: 'root' })
export class RepairModeService {
  private repairModeFilterFormValue: RepairModeFilterFormValue | null = null;

  constructor(private readonly httpClient: HttpClient) {}

  getRepairModeList(
    page: number,
    pageLimit: number,
    sort?: string,
    filters?: RepairModeFilter,
  ): Observable<HttpResponse<RepairModeListItem[]>> {
    const params: Record<string, string | number | string[] | boolean[]> = {
      page,
      limit: pageLimit > 0 ? pageLimit : 100,
    };
    if (sort) params['sort'] = sort;
    if (filters?.repairModeCodeList?.length) {
      const modeCodes = filters.repairModeCodeList.map((c) => c.split(' - ')[0]);
      params['RepairModeCodeList'] = modeCodes.join(',');
    }
    if (filters?.activeStatusesList?.length) params['ActiveStatusesList'] = filters.activeStatusesList.map(String).join(',');
    if (filters?.SearchText) params['SearchText'] = filters.SearchText;

    const url = utils.GetMasterDataManagementUrl('repair-modes');
    return this.httpClient
      .get<RepairModeListItem[]>(url, { params, observe: 'response' })
      .pipe(catchError(() => of(new HttpResponse<RepairModeListItem[]>({ body: [], headers: undefined }))));
  }

  getRepairModeByMode(mode: string): Observable<RepairModeListItem | null> {
    const url = utils.GetMasterDataManagementUrl(`repair-modes/${encodeURIComponent(mode)}`);
    return this.httpClient.get<RepairModeListItem>(url).pipe(catchError(() => of(null)));
  }

  getAllModeDescriptions(): Observable<CodeDescription[]> {
    const url = utils.GetMasterDataManagementUrl('repair-modes/mode-description/all');
    return this.httpClient.get<CodeDescription[]>(url).pipe(catchError(() => of([])));
  }

  createRepairMode(request: CreateRepairModeRequest): Observable<RepairModeListItem> {
    const url = utils.GetMasterDataManagementUrl('repair-modes');
    return this.httpClient.post<RepairModeListItem>(url, request);
  }

  updateRepairMode(mode: string, request: UpdateRepairModeRequest): Observable<RepairModeListItem> {
    const url = utils.GetMasterDataManagementUrl(`repair-modes/${encodeURIComponent(mode)}`);
    return this.httpClient.put<RepairModeListItem>(url, request);
  }

  getAuditLogs(
    mode: string,
    isSystemLog: boolean = false,
    page: number = 1,
    limit: number = 100,
  ): Observable<HttpResponse<AuditLog[]>> {
    const path = `repair-modes/${encodeURIComponent(mode)}/audit-logs?isSystemLog=${isSystemLog}&page=${page}&limit=${limit}`;
    return this.httpClient
      .get<AuditLog[]>(utils.GetMasterDataManagementUrl(path), { observe: 'response' })
      .pipe(catchError(() => of(new HttpResponse<AuditLog[]>({ body: [], status: 200, statusText: 'OK' }))));
  }

  downloadToExcel(request: DownloadRequest): Observable<Blob> {
    const url = utils.GetDownloadUrl('repair-mode');
    return this.httpClient.post(url, request, { responseType: 'blob' });
  }

  setRepairModeFilterFormValue(value: RepairModeFilterFormValue | null): void {
    this.repairModeFilterFormValue = value;
  }

  getRepairModeFilterFormValue(): RepairModeFilterFormValue | null {
    return this.repairModeFilterFormValue;
  }
}
